import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaPlus, FaTasks, FaFileAlt, FaUserPlus, FaCalendarPlus, FaAddressCard } from 'react-icons/fa';

interface QuickAddOption {
  id: string;
  label: string;
  icon: React.ReactNode;
  action: () => void;
}

export default function QuickAddButton(): React.JSX.Element {
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const [hoveredOption, setHoveredOption] = useState<string | null>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);

  const options: QuickAddOption[] = [
    {
      id: 'task',
      label: 'Task',
      icon: <FaTasks size={12} />,
      action: () => {
        navigate('/sales?addTask=true');
        setIsOpen(false);
      },
    },
    {
      id: 'offer',
      label: 'Offer',
      icon: <FaFileAlt size={12} />,
      action: () => {
        navigate('/offers/new');
        setIsOpen(false);
      },
    },
    {
      id: 'contact',
      label: 'Contact',
      icon: <FaAddressCard size={12} />,
      action: () => {
        navigate('/contacts?addContact=true');
        setIsOpen(false);
      },
    },
    {
      id: 'shift',
      label: 'Shift',
      icon: <FaCalendarPlus size={12} />,
      action: () => {
        navigate('/shiftplan?addShift=true');
        setIsOpen(false);
      },
    },
    {
      id: 'employee',
      label: 'Employee',
      icon: <FaUserPlus size={12} />,
      action: () => {
        navigate('/employees?addEmployee=true');
        setIsOpen(false);
      },
    },
  ];

  useEffect(() => {
    function handleClickOutside(event: MouseEvent): void {
      if (
        menuRef.current &&
        !menuRef.current.contains(event.target as Node) &&
        buttonRef.current &&
        !buttonRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    function handleEscape(event: KeyboardEvent): void {
      if (event.key === 'Escape') {
        setIsOpen(false);
      }
    }

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, []);

  const buttonSize = isHovered ? 37 : 34;

  return (
    <div
      style={{
        position: 'fixed',
        top: '20px',
        left: 'calc(72px + (100% - 72px) / 2)',
        transform: 'translateX(-50%)',
        zIndex: 1000,
        pointerEvents: 'none',
      }}
    >
      <button
        ref={buttonRef}
        onClick={() => setIsOpen(!isOpen)}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        style={{
          width: `${buttonSize}px`,
          height: `${buttonSize}px`,
          borderRadius: '50%',
          background: 'linear-gradient(135deg, #5271A5 0%, #3b5998 50%, #2c4a80 100%)',
          border: '1px solid rgba(255, 255, 255, 0.3)',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          boxShadow: isHovered
            ? '0 8px 25px rgba(82, 113, 165, 0.5), 0 0 15px rgba(82, 113, 165, 0.3)'
            : '0 4px 15px rgba(82, 113, 165, 0.4)',
          transition: 'all 0.2s ease',
          transform: isOpen ? 'rotate(45deg)' : 'rotate(0deg)',
          pointerEvents: 'auto',
        }}
        aria-label="Quick add"
        aria-expanded={isOpen}
        aria-haspopup="true"
      >
        <FaPlus size={13} color="white" style={{ filter: 'drop-shadow(0 1px 2px rgba(0,0,0,0.2))' }} />
      </button>

      {isOpen && (
        <div
          ref={menuRef}
          style={{
            position: 'absolute',
            top: '44px',
            left: '50%',
            transform: 'translateX(-50%)',
            background: 'rgba(255, 255, 255, 0.95)',
            backdropFilter: 'blur(12px)',
            WebkitBackdropFilter: 'blur(12px)',
            borderRadius: '12px',
            border: '1px solid rgba(255, 255, 255, 0.8)',
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12), 0 2px 8px rgba(0, 0, 0, 0.08)',
            minWidth: '160px',
            overflow: 'hidden',
            pointerEvents: 'auto',
          }}
        >
          <div
            style={{
              padding: '10px 14px 8px',
              fontSize: '10px',
              fontWeight: 600,
              color: '#64748b',
              textTransform: 'uppercase',
              letterSpacing: '0.5px',
              borderBottom: '1px solid rgba(0, 0, 0, 0.06)',
            }}
          >
            Quick Add
          </div>
          {options.map((option) => {
            const isOptionHovered = hoveredOption === option.id;
            return (
              <button
                key={option.id}
                onClick={option.action}
                onMouseEnter={() => setHoveredOption(option.id)}
                onMouseLeave={() => setHoveredOption(null)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '10px',
                  width: '100%',
                  padding: '10px 14px',
                  background: isOptionHovered ? 'rgba(82, 113, 165, 0.08)' : 'transparent',
                  border: 'none',
                  cursor: 'pointer',
                  fontSize: '12px',
                  fontWeight: 500,
                  color: isOptionHovered ? '#5271A5' : '#334155',
                  textAlign: 'left',
                  transition: 'all 0.15s',
                }}
              >
                <span
                  style={{
                    width: '26px',
                    height: '26px',
                    borderRadius: '8px',
                    background: isOptionHovered
                      ? 'linear-gradient(135deg, #5271A5 0%, #6384b8 100%)'
                      : 'rgba(82, 113, 165, 0.1)',
                    color: isOptionHovered ? 'white' : '#5271A5',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    transition: 'all 0.15s',
                    boxShadow: isOptionHovered ? '0 2px 8px rgba(82, 113, 165, 0.3)' : 'none',
                  }}
                >
                  {option.icon}
                </span>
                {option.label}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
